import { Button } from '@autho/ui';
import { useQueryClient } from '@tanstack/react-query';
import type { FormEvent, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useMe } from '../lib/api.ts';
import { EidButtons, EidReturnSignIn, EidSignUpFinish, eidIntent, eidReturnOf } from './Eid.tsx';
import { useAuth } from '../lib/auth.tsx';
import { LanguageProvider } from '../lib/language.tsx';
import { useOnline } from '../lib/online.ts';
import type { Trouble } from '../lib/trouble.ts';
import { TROUBLE_MESSAGE, signInTrouble, troubleOf } from '../lib/trouble.ts';
import { usePreferences, useT } from '../lib/preferences.tsx';
import { LANDING } from '../lib/profile.ts';
import { pageToOpen, refusalOnce, takeSignInReturn } from '../lib/sign-in-return.ts';
import { RETURNABLE, pendingReturn, rememberReturn, takeReturn } from '../lib/return-to.ts';
import { TenantProvider, activeTenant, storeTenant, storedTenant } from '../lib/tenant.tsx';
import { router } from '../router.tsx';

function Centered({ children }: { children: ReactNode }) {
  return (
    <main className="grid min-h-full place-items-center p-6">
      <div className="w-full max-w-sm rounded-lg border border-border bg-surface p-6">
        {children}
      </div>
    </main>
  );
}

/** A screen that is only a sentence. `fault` for one that is wrong rather than in progress. */
function Status({ message, fault = false }: { message: string; fault?: boolean }) {
  return (
    <main
      className={`grid min-h-full place-items-center p-6 text-sm ${fault ? 'text-danger' : 'text-muted'}`}
      role={fault ? 'alert' : 'status'}
      data-testid={fault ? 'gate-fault' : undefined}
    >
      {message}
    </main>
  );
}

function SignIn() {
  const t = useT();
  const { state, signInWithEmail, signInWithProvider } = useAuth();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState<string | null>(null);
  /** Why it did not work, not merely that it did not (F202). */
  const [failed, setFailed] = useState<Trouble | null>(null);
  const online = useOnline();
  const providers = state.status === 'signed-out' ? state.config.authProviders : [];
  const eid = state.status === 'signed-out' ? (state.config.eid ?? []) : [];
  /** Back from Vipps or BankID to sign in: completed here, before anybody is signed in (0127). */
  const returning = eidReturnOf(window.location.pathname);
  /** Back from Google or Microsoft without a session: why, in words, rather than the same screen again (row 14). */
  const [refusal] = useState(() => refusalOnce());

  const [sending, setSending] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    // **A second press cannot happen while the first is in flight.** The sign-in service allows one link
    // per address per second, so a double-click is enough to be refused — and the best message about a
    // refusal is the one nobody has to read (F202).
    if (sending) return;
    setSending(true);
    setFailed(null);
    try {
      const outcome = await signInWithEmail(email);
      if (outcome.ok) setSent(email);
      else setFailed(signInTrouble(outcome, online));
    } finally {
      setSending(false);
    }
  };

  if (returning !== null && eidIntent() === 'signin')
    return (
      <Centered>
        <h1 className="mb-4 text-title">{t('auth.signIn.title')}</h1>
        <EidReturnSignIn provider={returning} />
      </Centered>
    );

  return (
    <Centered>
      <h1 className="mb-4 text-title">{t('auth.signIn.title')}</h1>
      {refusal !== null && (
        <p role="alert" className="mb-4 text-sm text-danger" data-testid="provider-refusal">
          {t(
            refusal.kind === 'denied'
              ? 'auth.signIn.provider.denied'
              : 'auth.signIn.provider.failed',
            {
              provider: t(
                refusal.provider === null
                  ? 'auth.signIn.provider.unknown'
                  : `auth.signIn.provider.${refusal.provider}`,
              ),
            },
          )}
        </p>
      )}
      <EidButtons offered={eid} />
      {/* Offered only when the server says the provider exists. A button that cannot work is a worse
          answer than no button: on 29 September both were on screen and neither was enabled in Supabase. */}
      {providers.length > 0 && (
        <div className="mb-4 flex flex-col gap-2">
          {(['google', 'azure'] as const)
            .filter((provider) => providers.includes(provider))
            .map((provider) => (
              <Button key={provider} onClick={() => void signInWithProvider(provider)}>
                {t(provider === 'google' ? 'auth.signIn.google' : 'auth.signIn.microsoft')}
              </Button>
            ))}
        </div>
      )}
      {sent ? (
        <p role="status" className="text-sm">
          {t('auth.signIn.email.sent', { email: sent })}
        </p>
      ) : (
        <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-2">
          <label htmlFor="email" className="text-xs font-medium text-muted">
            {t('auth.signIn.email.label')}
          </label>
          <input
            id="email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="h-8 rounded border border-border bg-surface px-2 text-sm focus-visible:outline-2 focus-visible:outline-accent"
          />
          <Button type="submit" variant="primary" disabled={sending}>
            {t('auth.signIn.email.submit')}
          </Button>
          {failed !== null && (
            <p
              role={failed === 'too_soon' ? 'status' : 'alert'}
              data-testid="signin-trouble"
              // Not `danger` for something the person can simply wait out: colour is part of what a
              // screen says, and a red alert for *you were quick* reads as a fault in the product.
              className={`text-sm ${failed === 'too_soon' ? 'text-muted' : 'text-danger'}`}
            >
              {t(TROUBLE_MESSAGE[failed])}
            </p>
          )}
        </form>
      )}
    </Centered>
  );
}

/**
 * Auth → memberships → tenant. Everything behind this renders inside a tenant.
 *
 * **There is no first-run question any more** (R1; Thomas, 2026-10-08: D81). Every login has a Me, made by the
 * server on the first `GET /api/me`, so a new person lands in their own profile and adds a company from
 * Settings, by its organisation number. The chooser that asked *just me or a company?* is gone with the
 * question: a list with no profile in it is now a fault, and it is said as one.
 */
export function Gate({ children }: { children: ReactNode }) {
  const t = useT();
  const { state, signOut } = useAuth();
  const online = useOnline();
  const { locale } = usePreferences();
  const signedIn = state.status === 'signed-in';
  const me = useMe(signedIn, locale);
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(() => storedTenant());
  /** The profile just chosen, until it is the open one and has been opened on its landing page. */
  const arriving = useRef<string | null>(null);

  const memberships = me.data?.memberships ?? [];
  // Personal first in the list (R4), so a device with nothing stored opens the person's own profile.
  const membership = memberships.find((m) => m.tenantId === selected) ?? memberships[0];

  /**
   * **A profile is opened on its own landing page, with no scope from the one before** (R4). The screen that was
   * open may not exist in the profile chosen — Bank in a person's own profile, Relationships in a company's — and
   * a `?scope=` names the other profile's companies. So the address is moved once the choice is the open profile,
   * after the render that made it so: moved before, the route guard would judge the new address by the old kind.
   */
  const openTenantId = membership?.tenantId;
  const openKind = membership?.tenantKind;
  useEffect(() => {
    if (arriving.current === null || openTenantId !== arriving.current || !openKind) return;
    arriving.current = null;
    void router.navigate({ to: LANDING[openKind], search: { scope: undefined } });
  }, [openTenantId, openKind]);

  /**
   * **Back from Google or Microsoft, on the page the person left** (row 14). The provider can only send them to the
   * origin (Supabase's list of return addresses is exact), so the page is opened here, once they are signed in and
   * their profile is open; the kept path is forgotten as it is read, and only one of the app's own pages is ever kept.
   */
  useEffect(() => {
    if (!openTenantId) return;
    // Declared before F548's effect below, so it reads a waiting consent screen before that effect takes it.
    const to = pageToOpen(takeSignInReturn(), {
      consentWaiting: pendingReturn(Date.now()),
      onConsent: RETURNABLE.test(window.location.pathname),
      here: window.location.pathname,
    });
    if (to !== null) router.history.push(to);
  }, [openTenantId]);

  /**
   * **Back to a waiting Sign in with Autho** (F548): signed out on its consent screen, the address is remembered; once
   * signed in, wherever the way in landed, the browser goes back to it while its sign-in can still be answered.
   */
  const signedOut = state.status === 'signed-out';
  useEffect(() => {
    if (signedOut) rememberReturn(window.location.pathname, Date.now());
  }, [signedOut]);
  const ready = signedIn && membership !== undefined;
  useEffect(() => {
    if (!ready) return;
    const back = takeReturn(Date.now());
    if (back === null || window.location.pathname === back) return;
    const uid = back.slice(back.lastIndexOf('/') + 1);
    void router.navigate({ to: '/sign-in-with-autho/$uid', params: { uid }, replace: true });
  }, [ready]);

  // Signing out is an action taken in response to the server's answer, not state derived from it: the
  // stored session is the thing that is wrong, and clearing it is what returns a person to the sign-in
  // screen rather than leaving them reading about it.
  const expired = me.isError && troubleOf(me.error, online) === 'expired';
  useEffect(() => {
    if (expired) void signOut();
  }, [expired, signOut]);

  // The language is the person's, not a profile's (Thomas, 2026-10-09): the saved one is applied by the
  // `LanguageProvider` below from `GET /api/me`'s `locale`, and choosing another profile never changes it.

  if (state.status === 'loading') return <Status message={t('app.loading')} />;
  if (state.status === 'unconfigured')
    return <Status message={t('auth.signIn.providerUnavailable', { provider: 'Supabase' })} />;
  if (state.status === 'error')
    return <Status message={t(TROUBLE_MESSAGE[troubleOf(state.because, online)])} />;
  if (state.status === 'signed-out') return <SignIn />;
  if (me.isPending) return <Status message={t('app.loading')} />;
  // **A session the server will not accept is not a network problem.** It used to render as *check your
  // connection*; now the app does the one thing that fixes it and says so while it happens.
  if (me.isError) return <Status message={t(TROUBLE_MESSAGE[troubleOf(me.error, online)])} />;
  // The server makes the Me before it answers, so a list with nothing in it is a fault on Autho's side and is
  // said as one. There is nothing to choose and nothing a form could make: a Me is made by `GET /api/me` and by
  // nothing else (R1, D81).
  if (!membership) return <Status fault message={t('profile.missing')} />;

  // Set before children render so their first requests already carry the tenant header.
  activeTenant.set(membership.tenantId);

  return (
    <TenantProvider
      value={{
        userId: me.data.userId,
        email: me.data.email,
        emailVerified: me.data.emailVerified,
        tenantId: membership.tenantId,
        kind: membership.tenantKind,
        membership,
        memberships,
        canMutate: membership.role === 'owner' || membership.role === 'admin',
        chatOpen: me.data.chatOpen,
        selectTenant: (id, options) => {
          storeTenant(id);
          setSelected(id);
          // A page that switches the profile for its own sake (Sign in with Autho's consent screen, F548) stays where
          // it is; any other switch opens the profile on its landing page (R4).
          arriving.current = options?.stay ? null : id;
          /**
           * **Nothing on the open screen is fetched again under the profile chosen** (R4). This used to set the
           * tenant header here and refetch every open query at once: the screen still showing — Bank, with one
           * company's id in its address — asked the new profile for that company and was told 404, and an answer
           * fetched that way is kept under the old profile's key. Now the header changes when the new profile
           * renders (above), and what was read before is only marked stale, to be read again when it is next shown.
           */
          void qc.invalidateQueries({ queryKey: ['t'], refetchType: 'none' });
          // And a read of the profile being left that is still on its way is stopped, not left to land after the
          // switch under the old profile's key (isolation finding 9, F389).
          if (id !== membership.tenantId)
            void qc.cancelQueries({ queryKey: ['t', membership.tenantId] });
        },
      }}
    >
      <LanguageProvider person={me.data.userId} saved={me.data.locale}>
        {/* A BankID sign-up waiting in this browser is finished as soon as its link signs the person in (D51 row 8). */}
        <EidSignUpFinish />
        {children}
      </LanguageProvider>
    </TenantProvider>
  );
}
